import { useMutation } from '@tanstack/react-query';
import { useOptionalDependency } from 'ditox-react';
import { FIREBASE_MANAGER_TOKEN } from '@/container';
import { useCommDispatcher, useIntercomDispatcher } from '@/hooks/comm/use-comm';
import type { AuthType } from '@/models/auth';
import type { RespondedType, ResponseError } from '@/models/response';
import { useAuthStore } from '@/stores/auth';
import { fetcher } from '@/utils/fetcher';
import { queryClient } from '@/utils/query-client';

interface SignOutParams {
  credentials?: AuthType[] | undefined;
  reason?: string | false | undefined;
}

export default function useSignOut() {
  const { dispatch: commDispatch } = useCommDispatcher();
  const { dispatch: intercomDispatch } = useIntercomDispatcher();
  const firebaseManager = useOptionalDependency(FIREBASE_MANAGER_TOKEN);

  const disposeServices = () => {
    firebaseManager?.revokeDeviceToken().catch(console.warn);
    commDispatch({ event: 'sign-out' });
    intercomDispatch({ event: 'update-auth-credentials' });
  };

  const mutation = useMutation<void, ResponseError, SignOutParams>({
    mutationKey: ['/Logout'],
    mutationFn: async ({ credentials }) => {
      if (credentials) {
        await Promise.allSettled(
          credentials.map((cred) =>
            fetcher<RespondedType>(
              '/Logout',
              {
                UID: cred.UserID,
                Token: cred.Token,
              },
              { requireAuth: false },
            ),
          ),
        );
      } else {
        await fetcher('/Logout', undefined);
      }
    },
    onMutate: () => {
      disposeServices();
    },
    onSettled: (_data, _error, { credentials, reason }) => {
      const { stopAll, stop } = useAuthStore.getState();

      if (credentials) {
        stopAll(reason);
      } else {
        stop(reason);
      }
      intercomDispatch({ event: 'update-auth-credentials' });
    },
    onError: undefined,
    retry: false,
  });

  return {
    ...mutation,
    disposeServices,
  };
}
